import { useEffect, useState } from 'react';
import type { GlobalMessage } from './global-message.type';

type UseGlobalMessageReturnType = {
  globalMessage: GlobalMessage | null;
  setGlobalMessage: React.Dispatch<React.SetStateAction<GlobalMessage | null>>;
};

/**
 * Custom hook to manage global message state and listen to global message updates.
 *
 * @return {UseGlobalMessageReturnType} The global message state and its setter function.
 */
const useGlobalMessage = (): UseGlobalMessageReturnType => {
  const [globalMessage, setGlobalMessage] = useState<GlobalMessage | null>(null);

  // biome-ignore lint/correctness/useExhaustiveDependencies(setGlobalMessage): ignore
  useEffect(() => {
    const handleStorageChange = (event: Event) => {
      const message: GlobalMessage = (event as CustomEvent).detail;

      // If message has content, set it; otherwise clear it
      if (message?.message) {
        setGlobalMessage(message);
      } else {
        setGlobalMessage(null);
      }
    };

    window.addEventListener('globalMessagesStorage', handleStorageChange);

    return () => {
      window.removeEventListener('globalMessagesStorage', handleStorageChange);
    };
  }, [setGlobalMessage]);

  return {
    globalMessage,
    setGlobalMessage,
  };
};

export { useGlobalMessage };
